import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, User, Home, Users, Feather, Check, ArrowRight, ArrowLeft, X } from "lucide-react";
import api from "../api";
import { LogoLight } from "./Logo";

// ============================================================================
// AUPREA — Onboarding Wizard (premier remplissage guidé, 5 étapes)
// Étapes : Bienvenue → État civil → Biens & Comptes → Bénéficiaires → Volontés
// Design : modal plein-écran, chaque étape optionnelle (bouton "Passer"),
//          "Reprendre plus tard" ferme définitivement, "Terminer" en fin.
// Sauvegarde : via endpoints existants /carnet/profil, /carnet/enfants, /carnet/biens.
// ============================================================================

const NAVY = "#15294f";
const GOLD = "#caa86a";
const CREAM = "#f3efe6";
const INK = "#23262e";
const GREEN = "#2f7d5b";

const STEPS_META = [
  { id: "welcome", label: "Bienvenue", icon: Sparkles },
  { id: "etat_civil", label: "État civil", icon: User },
  { id: "biens_comptes", label: "Biens & Comptes", icon: Home },
  { id: "beneficiaires", label: "Bénéficiaires", icon: Users },
  { id: "volontes", label: "Volontés", icon: Feather },
];

export default function OnboardingWizard({ initialStep = 0, onFinished }) {
  const nav = useNavigate();
  const [step, setStep] = useState(Math.min(Math.max(initialStep, 0), 4));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form state per step (kept local, saved on "Suivant")
  const [etatCivil, setEtatCivil] = useState({
    prenom: "", nom: "", date_naissance: "", adresse_ville: "", telephone: "",
  });
  const [biens, setBiens] = useState({
    proprietaire: "",           // "Oui" / "Non"
    type_bien: "Résidence principale",
    ville_bien: "",
    valeur_bien: "",
    a_des_comptes: "",          // "Oui" / "Non"
  });
  const [benef, setBenef] = useState({
    statut_couple: "",
    a_des_enfants: "",          // "Oui" / "Non"
    enfant_prenom: "",
    enfant_nom: "",
    enfant_date_naissance: "",
  });
  const [volontes, setVolontes] = useState({
    type_obseques: "",
    don_organes: "",
    has_notaire: "",
  });

  // Push current step to backend (best-effort)
  useEffect(() => {
    api.post("/onboarding/progress", { step }).catch(() => {});
  }, [step]);

  const saveField = async (rubrique, clef, valeur) => {
    if (!valeur || String(valeur).trim() === "") return;
    try {
      await api.post("/onboarding/save-field", { rubrique, clef, valeur: String(valeur).trim() });
    } catch (_e) { /* best-effort */ }
  };

  const handleNextEtatCivil = async () => {
    setSaving(true);
    setError("");
    try {
      await Promise.all([
        saveField("identite", "prenom", etatCivil.prenom),
        saveField("identite", "nom", etatCivil.nom),
        saveField("identite", "date_naissance", etatCivil.date_naissance),
        saveField("identite", "adresse_ville", etatCivil.adresse_ville),
        saveField("identite", "telephone", etatCivil.telephone),
      ]);
      setStep(2);
    } catch (e) {
      setError("Impossible de sauvegarder pour l'instant, réessayez.");
    } finally { setSaving(false); }
  };

  const handleNextBiens = async () => {
    setSaving(true);
    setError("");
    try {
      // Add a "bien" if user is proprietaire + provided info (via endpoint onboarding)
      if (/oui/i.test(biens.proprietaire) && (biens.ville_bien || biens.valeur_bien)) {
        try {
          await api.post("/onboarding/add-bien", {
            type_bien: biens.type_bien,
            ville: biens.ville_bien,
            valeur: parseFloat(biens.valeur_bien) || 0,
          });
        } catch (_e) { /* best-effort */ }
      }
      // Save "a des comptes" info flag on rubrique patrimoine_financier
      if (biens.a_des_comptes) {
        await saveField("patrimoine_financier", "a_des_comptes_onboarding", biens.a_des_comptes);
      }
      setStep(3);
    } catch (e) {
      setError("Impossible de sauvegarder pour l'instant, réessayez.");
    } finally { setSaving(false); }
  };

  const handleNextBenef = async () => {
    setSaving(true);
    setError("");
    try {
      if (benef.statut_couple) {
        await saveField("couple", "statut", benef.statut_couple);
      }
      if (benef.a_des_enfants) {
        await saveField("famille", "a_des_enfants", benef.a_des_enfants);
      }
      if (/oui/i.test(benef.a_des_enfants) && (benef.enfant_prenom || benef.enfant_nom)) {
        try {
          await api.post("/onboarding/add-enfant", {
            prenom: benef.enfant_prenom || "",
            nom: benef.enfant_nom || "",
            date_naissance: benef.enfant_date_naissance || "",
          });
        } catch (_e) { /* best-effort */ }
      }
      setStep(4);
    } catch (e) {
      setError("Impossible de sauvegarder pour l'instant, réessayez.");
    } finally { setSaving(false); }
  };

  const handleFinish = async () => {
    setSaving(true);
    setError("");
    try {
      if (volontes.type_obseques) await saveField("dernieres_volontes", "type_obseques", volontes.type_obseques);
      if (volontes.don_organes) {
        const map = {
          "Favorable": "Je suis favorable au don de mes organes",
          "Refus": "Je ne souhaite pas donner mes organes",
          "À discuter": "Je préfère en parler avec mes proches",
        };
        await saveField("sante", "position_don_organes", map[volontes.don_organes] || volontes.don_organes);
      }
      if (volontes.has_notaire) await saveField("personnes_confiance", "has_notaire", volontes.has_notaire);
      await api.post("/onboarding/complete");
      onFinished && onFinished();
      nav("/carnet/identite");
    } catch (e) {
      setError("Impossible de finaliser pour l'instant, réessayez.");
    } finally { setSaving(false); }
  };

  const handleSkipStep = () => {
    if (step < 4) setStep(step + 1);
    else handleFinish();
  };

  const handleLaterClose = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/skip-all");
    } catch (_e) { /* ignore */ }
    setSaving(false);
    onFinished && onFinished();
  };

  // Full-screen modal container styling
  return (
    <div
      data-testid="onboarding-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15,20,35,0.55)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, overflowY: "auto",
        fontFamily: "'Archivo', sans-serif",
      }}
    >
      <div
        style={{
          background: CREAM,
          width: "100%", maxWidth: 780,
          maxHeight: "94vh",
          borderRadius: 4,
          boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header : navy bar with logo + stepper */}
        <div style={{ background: NAVY, color: CREAM, padding: "20px 28px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <LogoLight size={24} />
            <button
              onClick={handleLaterClose}
              disabled={saving}
              data-testid="onboarding-close"
              title="Reprendre plus tard"
              style={{ color: CREAM, opacity: 0.7, display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
            >
              <X size={16} /> Reprendre plus tard
            </button>
          </div>
          <Stepper current={step} />
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "36px 32px 28px" }}>
          {step === 0 && <StepWelcome onStart={() => setStep(1)} />}
          {step === 1 && (
            <StepEtatCivil
              values={etatCivil}
              setValues={setEtatCivil}
            />
          )}
          {step === 2 && (
            <StepBiens
              values={biens}
              setValues={setBiens}
            />
          )}
          {step === 3 && (
            <StepBenef
              values={benef}
              setValues={setBenef}
            />
          )}
          {step === 4 && (
            <StepVolontes
              values={volontes}
              setValues={setVolontes}
            />
          )}
          {error && (
            <div data-testid="onboarding-error" style={{ marginTop: 20, padding: 12, background: "#fdecea", border: "1px solid #ea8b7c", color: "#7a1e0d", fontSize: 14 }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <div style={{ borderTop: `1px solid rgba(21,41,79,0.12)`, padding: "16px 28px", background: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: "rgba(35,38,46,0.55)" }}>
            Étape <strong>{step + 1}</strong> sur 5
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {step > 0 && step < 5 && (
              <button
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={saving}
                data-testid="onboarding-back"
                style={{ padding: "10px 18px", color: NAVY, background: "transparent", border: `1px solid ${NAVY}`, display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}
              >
                <ArrowLeft size={15} /> Retour
              </button>
            )}
            {step > 0 && step < 4 && (
              <button
                onClick={handleSkipStep}
                disabled={saving}
                data-testid="onboarding-skip"
                style={{ padding: "10px 18px", color: "rgba(35,38,46,0.6)", background: "transparent", border: `1px solid rgba(35,38,46,0.2)`, fontSize: 14 }}
              >
                Passer cette étape
              </button>
            )}
            {step === 0 && (
              <button
                onClick={() => setStep(1)}
                data-testid="onboarding-start"
                style={ctaStyle()}
              >
                Commencer <ArrowRight size={15} />
              </button>
            )}
            {step === 1 && (
              <button onClick={handleNextEtatCivil} disabled={saving} data-testid="onboarding-next-etat-civil" style={ctaStyle()}>
                {saving ? "…" : (<>Suivant <ArrowRight size={15} /></>)}
              </button>
            )}
            {step === 2 && (
              <button onClick={handleNextBiens} disabled={saving} data-testid="onboarding-next-biens" style={ctaStyle()}>
                {saving ? "…" : (<>Suivant <ArrowRight size={15} /></>)}
              </button>
            )}
            {step === 3 && (
              <button onClick={handleNextBenef} disabled={saving} data-testid="onboarding-next-benef" style={ctaStyle()}>
                {saving ? "…" : (<>Suivant <ArrowRight size={15} /></>)}
              </button>
            )}
            {step === 4 && (
              <button onClick={handleFinish} disabled={saving} data-testid="onboarding-finish" style={{ ...ctaStyle(), background: GREEN, borderColor: GREEN }}>
                {saving ? "…" : (<>Terminer <Check size={16} /></>)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ctaStyle() {
  return {
    padding: "11px 22px", background: NAVY, color: CREAM,
    border: `1px solid ${NAVY}`,
    display: "flex", alignItems: "center", gap: 8,
    fontSize: 14, fontWeight: 600, letterSpacing: "0.02em",
  };
}

// ============ Stepper (progress indicator) =================================
function Stepper({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid="onboarding-stepper">
      {STEPS_META.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={s.id}>
            <div
              data-testid={`stepper-node-${i}`}
              style={{
                display: "flex", alignItems: "center", gap: 8, flex: active ? 2 : 1,
                minWidth: 0,
              }}
            >
              <div style={{
                width: 26, height: 26, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: done ? GOLD : (active ? "#fff" : "rgba(255,255,255,0.15)"),
                color: done ? NAVY : (active ? NAVY : "rgba(255,255,255,0.65)"),
                border: active ? `1.5px solid ${GOLD}` : "none",
                fontSize: 13, fontWeight: 700,
                flexShrink: 0,
              }}>
                {done ? <Check size={13} /> : i + 1}
              </div>
              {active && (
                <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: GOLD, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {s.label}
                </span>
              )}
            </div>
            {i < STEPS_META.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? GOLD : "rgba(255,255,255,0.15)" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ============ Steps ========================================================

function H({ children }) {
  return <h2 style={{ fontFamily: "Spectral, serif", fontSize: 28, fontWeight: 500, color: NAVY, marginBottom: 10, lineHeight: 1.2 }}>{children}</h2>;
}
function Sub({ children }) {
  return <p style={{ fontSize: 15, color: "rgba(35,38,46,0.7)", lineHeight: 1.55, marginBottom: 24 }}>{children}</p>;
}
function Label({ children }) {
  return <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 6, letterSpacing: "0.02em" }}>{children}</label>;
}
function Input(props) {
  return <input {...props} style={{ width: "100%", padding: "10px 12px", fontSize: 15, background: "#fff", border: `1px solid rgba(21,41,79,0.2)`, color: INK, borderRadius: 3, ...(props.style || {}) }} />;
}
function Select({ children, ...props }) {
  return <select {...props} style={{ width: "100%", padding: "10px 12px", fontSize: 15, background: "#fff", border: `1px solid rgba(21,41,79,0.2)`, color: INK, borderRadius: 3, ...(props.style || {}) }}>{children}</select>;
}

function StepWelcome({ onStart }) {
  return (
    <div style={{ textAlign: "center", padding: "24px 8px" }} data-testid="step-welcome">
      <div style={{ width: 68, height: 68, borderRadius: "50%", background: `${GOLD}20`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", border: `1px solid ${GOLD}` }}>
        <Sparkles size={30} style={{ color: GOLD }} />
      </div>
      <H>Bienvenue sur AUPRÉA</H>
      <p style={{ fontFamily: "Spectral, serif", fontSize: 18, fontStyle: "italic", color: GOLD, marginBottom: 22 }}>« Anticiper est un acte d&apos;amour. »</p>
      <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "left", background: "#fff", border: `1px solid rgba(21,41,79,0.1)`, padding: 22, borderLeft: `3px solid ${GOLD}` }}>
        <p style={{ fontSize: 15, color: INK, lineHeight: 1.65, marginBottom: 14 }}>
          En <strong>5 étapes rapides</strong>, nous allons poser les fondations essentielles de votre carnet successoral.
        </p>
        <ul style={{ listStyle: "none", padding: 0, fontSize: 14, color: "rgba(35,38,46,0.8)", lineHeight: 1.9 }}>
          <li><strong style={{ color: NAVY }}>1.</strong> Votre <strong>état civil</strong></li>
          <li><strong style={{ color: NAVY }}>2.</strong> Vos <strong>biens & comptes</strong> principaux</li>
          <li><strong style={{ color: NAVY }}>3.</strong> Vos <strong>bénéficiaires</strong> (conjoint, enfants)</li>
          <li><strong style={{ color: NAVY }}>4.</strong> Vos <strong>volontés</strong></li>
        </ul>
        <p style={{ fontSize: 13, color: "rgba(35,38,46,0.55)", marginTop: 16 }}>
          ⏱ Environ 3 minutes. Chaque étape peut être <em>passée</em> et complétée plus tard depuis votre carnet.
        </p>
      </div>
    </div>
  );
}

function StepEtatCivil({ values, setValues }) {
  const upd = (k, v) => setValues({ ...values, [k]: v });
  return (
    <div data-testid="step-etat-civil">
      <H>Votre état civil</H>
      <Sub>Quelques informations pour personnaliser votre carnet et vos alertes successorales.</Sub>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <Label>Prénom</Label>
          <Input data-testid="onb-prenom" value={values.prenom} onChange={(e) => upd("prenom", e.target.value)} placeholder="Ex. Marie" />
        </div>
        <div>
          <Label>Nom de famille</Label>
          <Input data-testid="onb-nom" value={values.nom} onChange={(e) => upd("nom", e.target.value)} placeholder="Ex. Dupont" />
        </div>
        <div>
          <Label>Date de naissance</Label>
          <Input data-testid="onb-dob" type="date" value={values.date_naissance} onChange={(e) => upd("date_naissance", e.target.value)} />
        </div>
        <div>
          <Label>Ville</Label>
          <Input data-testid="onb-ville" value={values.adresse_ville} onChange={(e) => upd("adresse_ville", e.target.value)} placeholder="Ex. Pointe-à-Pitre" />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Label>Téléphone (facultatif)</Label>
          <Input data-testid="onb-tel" value={values.telephone} onChange={(e) => upd("telephone", e.target.value)} placeholder="06 __ __ __ __" />
        </div>
      </div>
    </div>
  );
}

function StepBiens({ values, setValues }) {
  const upd = (k, v) => setValues({ ...values, [k]: v });
  const isOwner = /oui/i.test(values.proprietaire);
  return (
    <div data-testid="step-biens">
      <H>Vos biens & comptes</H>
      <Sub>Un aperçu rapide de votre patrimoine — vous pourrez tout détailler ensuite.</Sub>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <Label>Êtes-vous propriétaire d&apos;un bien immobilier ?</Label>
          <Select data-testid="onb-proprietaire" value={values.proprietaire} onChange={(e) => upd("proprietaire", e.target.value)}>
            <option value="">— Sélectionner —</option>
            <option value="Oui">Oui</option>
            <option value="Non">Non</option>
          </Select>
        </div>
        {isOwner && (
          <div style={{ background: "#fff", border: `1px solid rgba(21,41,79,0.1)`, borderLeft: `3px solid ${GOLD}`, padding: 18 }}>
            <p style={{ fontSize: 13, color: NAVY, fontWeight: 600, marginBottom: 12 }}>Ajoutez votre bien principal (facultatif)</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Type</Label>
                <Select data-testid="onb-type-bien" value={values.type_bien} onChange={(e) => upd("type_bien", e.target.value)}>
                  <option>Résidence principale</option>
                  <option>Résidence secondaire</option>
                  <option>Investissement locatif</option>
                  <option>Terrain / foncier</option>
                  <option>Autre</option>
                </Select>
              </div>
              <div>
                <Label>Ville / localisation</Label>
                <Input data-testid="onb-ville-bien" value={values.ville_bien} onChange={(e) => upd("ville_bien", e.target.value)} placeholder="Ex. Bordeaux" />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <Label>Valeur estimée (€)</Label>
                <Input data-testid="onb-valeur-bien" type="number" value={values.valeur_bien} onChange={(e) => upd("valeur_bien", e.target.value)} placeholder="Ex. 250000" />
              </div>
            </div>
          </div>
        )}
        <div>
          <Label>Avez-vous un ou plusieurs comptes bancaires / livrets d&apos;épargne ?</Label>
          <Select data-testid="onb-a-comptes" value={values.a_des_comptes} onChange={(e) => upd("a_des_comptes", e.target.value)}>
            <option value="">— Sélectionner —</option>
            <option value="Oui">Oui</option>
            <option value="Non">Non</option>
          </Select>
          {/oui/i.test(values.a_des_comptes) && (
            <p style={{ fontSize: 13, color: "rgba(35,38,46,0.65)", marginTop: 10, fontStyle: "italic" }}>
              Vous pourrez ajouter chaque compte en détail dans la rubrique <strong>Patrimoine financier</strong> de votre carnet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function StepBenef({ values, setValues }) {
  const upd = (k, v) => setValues({ ...values, [k]: v });
  const hasKids = /oui/i.test(values.a_des_enfants);
  return (
    <div data-testid="step-benef">
      <H>Vos bénéficiaires</H>
      <Sub>Votre situation familiale nous permet d&apos;anticiper la répartition de vos droits successoraux.</Sub>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <Label>Votre situation de couple</Label>
          <Select data-testid="onb-statut-couple" value={values.statut_couple} onChange={(e) => upd("statut_couple", e.target.value)}>
            <option value="">— Sélectionner —</option>
            <option>Célibataire</option>
            <option>Marié(e)</option>
            <option>PACS</option>
            <option>Concubinage</option>
            <option>Divorcé(e)</option>
            <option>Veuf/Veuve</option>
          </Select>
        </div>
        <div>
          <Label>Avez-vous des enfants ?</Label>
          <Select data-testid="onb-a-enfants" value={values.a_des_enfants} onChange={(e) => upd("a_des_enfants", e.target.value)}>
            <option value="">— Sélectionner —</option>
            <option value="Oui">Oui</option>
            <option value="Non">Non</option>
          </Select>
        </div>
        {hasKids && (
          <div style={{ background: "#fff", border: `1px solid rgba(21,41,79,0.1)`, borderLeft: `3px solid ${GOLD}`, padding: 18 }}>
            <p style={{ fontSize: 13, color: NAVY, fontWeight: 600, marginBottom: 12 }}>Ajoutez votre premier enfant (facultatif)</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <Label>Prénom</Label>
                <Input data-testid="onb-enfant-prenom" value={values.enfant_prenom} onChange={(e) => upd("enfant_prenom", e.target.value)} />
              </div>
              <div>
                <Label>Nom</Label>
                <Input data-testid="onb-enfant-nom" value={values.enfant_nom} onChange={(e) => upd("enfant_nom", e.target.value)} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <Label>Date de naissance</Label>
                <Input data-testid="onb-enfant-dob" type="date" value={values.enfant_date_naissance} onChange={(e) => upd("enfant_date_naissance", e.target.value)} />
              </div>
            </div>
            <p style={{ fontSize: 12, color: "rgba(35,38,46,0.55)", marginTop: 10, fontStyle: "italic" }}>
              Ajoutez les autres enfants ultérieurement depuis la rubrique <strong>Famille</strong>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StepVolontes({ values, setValues }) {
  const upd = (k, v) => setValues({ ...values, [k]: v });
  return (
    <div data-testid="step-volontes">
      <H>Vos volontés essentielles</H>
      <Sub>Ces choix permettront de guider vos proches le moment venu. Vous pouvez évidemment y revenir à tout moment.</Sub>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <Label>Type d&apos;obsèques préféré</Label>
          <Select data-testid="onb-obseques" value={values.type_obseques} onChange={(e) => upd("type_obseques", e.target.value)}>
            <option value="">— Sélectionner —</option>
            <option>Inhumation</option>
            <option>Crémation</option>
            <option>Indifférent</option>
          </Select>
        </div>
        <div>
          <Label>Position sur le don d&apos;organes</Label>
          <Select data-testid="onb-don-organes" value={values.don_organes} onChange={(e) => upd("don_organes", e.target.value)}>
            <option value="">— Sélectionner —</option>
            <option>Favorable</option>
            <option>Refus</option>
            <option>À discuter</option>
          </Select>
        </div>
        <div>
          <Label>Avez-vous déjà un notaire ?</Label>
          <Select data-testid="onb-has-notaire" value={values.has_notaire} onChange={(e) => upd("has_notaire", e.target.value)}>
            <option value="">— Sélectionner —</option>
            <option value="Oui">Oui</option>
            <option value="Non">Non — je souhaiterais en trouver un</option>
          </Select>
          {/non/i.test(values.has_notaire) && (
            <p style={{ fontSize: 13, color: "rgba(35,38,46,0.65)", marginTop: 10, fontStyle: "italic" }}>
              Vous pourrez contacter l&apos;un de nos notaires partenaires depuis l&apos;<strong>Annuaire pro</strong> à tout moment.
            </p>
          )}
        </div>
        <div style={{ background: `${GREEN}12`, border: `1px solid ${GREEN}55`, padding: 16, marginTop: 8 }}>
          <p style={{ fontSize: 14, color: NAVY, lineHeight: 1.55 }}>
            <strong style={{ color: GREEN }}>Bravo !</strong> Vous êtes prêt(e) à découvrir votre carnet. Cliquez sur <strong>Terminer</strong> pour accéder à votre tableau de bord.
          </p>
        </div>
      </div>
    </div>
  );
}
